// track() -- the single choke point for product analytics.
//
// Every event in the app flows through here. track() enriches the event with
// ambient dimensions (app_version, session_id, source/surface derived from the
// actor, and doc_kind/unit_system when a viewer has set them), runs the
// caller-supplied props through the privacy guard (whitelist + forbidden-key
// strip + bucketization), then fans the finished event out to every registered
// sink that accepts it.
//
// Call sites never touch a sink or PostHog directly, and never pass a raw
// `source` -- that is derived from the actor context (actor.ts).

import { getActor, getSurface } from './actor';
import type { EnrichedEvent, EventName, EventProps } from './events';
import { scrubProps } from './privacy';
import { initInputTracking } from './inputMode';
import { installCrashCapture } from '@/core/diagnostics/crashCapture';
import { debugSink } from './sinks/debugSink';
import { posthogSink } from './sinks/posthogSink';
import { ga4Sink } from './sinks/ga4Sink';
import type { EventSink } from './sinks/types';
import { initAnalytics } from '@/utils/analytics';

// Registered sinks. Add a CF Analytics Engine sink here -- nothing else in the
// pipeline changes. Order is delivery order. debug = local vetting (dev); posthog
// = product analysis (prod); ga4 = acquisition + fleet dashboard (prod).
const sinks: EventSink[] = [debugSink, posthogSink, ga4Sink];

// ---- session id: random, stable for the page load, in-memory only ----------
function makeSessionId(): string {
    try {
        if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
            return crypto.randomUUID();
        }
    } catch {
        // fall through
    }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
const sessionId = makeSessionId();

// ---- app version: /version.json (fetched once), else build var, else 'dev' --
function buildVersionFallback(): string {
    try {
        // __APP_VERSION__ is a vite `define` constant; guarded for test/SSR.
        if (typeof __APP_VERSION__ === 'string' && __APP_VERSION__) return __APP_VERSION__;
    } catch {
        // not defined in this environment
    }
    return 'dev';
}
let appVersion = buildVersionFallback();

function initVersion(): void {
    if (typeof fetch !== 'function' || typeof window === 'undefined') return;
    fetch('/version.json', { cache: 'no-store' })
        .then(r => (r.ok ? r.json() : null))
        .then((data: unknown) => {
            if (data && typeof data === 'object' && 'version' in data) {
                const v = (data as { version?: unknown }).version;
                if (typeof v === 'string' && v) appVersion = v;
            }
        })
        .catch(() => {
            // keep the fallback -- version.json is best-effort enrichment
        });
}

// ---- ambient context: doc_kind + unit_system set by the active viewer -------
// input_mode + device_class are set by inputMode.ts (see initTracking below).
type AmbientContext = {
    doc_kind?: string;
    unit_system?: string;
    input_mode?: string;
    device_class?: string;
};
let ambient: AmbientContext = {};

/** Set/merge the ambient dimensions stamped onto every subsequent event. */
export function setTrackContext(patch: AmbientContext): void {
    ambient = { ...ambient, ...patch };
}

/** Clear ambient dimensions (call when leaving a viewer). */
export function clearTrackContext(): void {
    ambient = {};
}

let versionInitStarted = false;
/** Kick off the one-time /version.json fetch and boot PostHog eagerly. Safe to call more than once. */
export function initTracking(): void {
    if (versionInitStarted) return;
    versionInitStarted = true;
    initVersion();
    // Stamp device_class now and input_mode on the first pointer interaction.
    // Injecting setTrackContext keeps the module graph acyclic (check:cycles).
    initInputTracking(setTrackContext);
    // The one crash pipeline (core/diagnostics/crashCapture.ts): the app's window error + unhandledrejection
    // listeners keep every crash on the device for the crash screen's Copy details, and report a rejection as
    // promise_rejection_unhandled (error_kind + code locations, never the message). `track` is injected (no cycle).
    installCrashCapture((props) => track('promise_rejection_unhandled', props));
    // Boot PostHog before the router renders so the recorder is running before any
    // restore or auth callback. Without this, events on the failure path (where
    // track() is never called before PostHog would otherwise init) are silently lost.
    void initAnalytics();
}

/**
 * Track a product event. Name + prop keys are compile-time checked against the
 * catalog; values are runtime-scrubbed + bucketized by the privacy guard.
 */
export function track(name: EventName, props?: EventProps): void {
    const scrubbed = scrubProps(name, props);

    // Ambient dimensions are enrichment (always allowed), added under the props
    // bag but AFTER the per-event whitelist filter. Explicit props win.
    const enrichedProps: Record<string, string | number | boolean> = {};
    if (ambient.doc_kind !== undefined) enrichedProps.doc_kind = ambient.doc_kind;
    if (ambient.unit_system !== undefined) enrichedProps.unit_system = ambient.unit_system;
    if (ambient.input_mode !== undefined) enrichedProps.input_mode = ambient.input_mode;
    if (ambient.device_class !== undefined) enrichedProps.device_class = ambient.device_class;
    Object.assign(enrichedProps, scrubbed);

    const event: EnrichedEvent = {
        event: name,
        timestamp: new Date().toISOString(),
        source: getActor(),
        surface: getSurface(),
        app_version: appVersion,
        session_id: sessionId,
        props: enrichedProps,
    };

    for (const sink of sinks) {
        try {
            if (sink.accepts(event)) sink.send(event);
        } catch {
            // a failing sink must never break the app or other sinks
        }
    }
}

/** Test/introspection helper -- the session id for this page load. */
export function getSessionId(): string {
    return sessionId;
}
